import { Link } from "react-router-dom";
import { ChevronDown, CheckCircle, Clock, FileText, Phone, Star } from "lucide-react";
import { Container } from "@/components/shared/Primitives";
import { openCalendly } from "@/components/shared/BookCallButton";
import { tint } from "@/data/site";

const FAQS = [
  {
    q: "Do I need any technical knowledge?",
    a: "Not at all. I do the technical work. You just need to know your business well enough to answer questions about how you run things.",
  },
  {
    q: "How is the call conducted?",
    a: "It's a standard video call. I'll send you a Calendly link after purchase. You pick a time that works. No prep needed.",
  },
  {
    q: "What if none of the tools work for me?",
    a: "That's covered by the guarantee. If you don't find at least one tool worth the investment, I'll refund every cent. No questions asked.",
  },
  {
    q: "How long does the whole process take?",
    a: "About 48 hours from our call. You get the report within two days, then we meet for a 30-minute follow-up to walk through it.",
  },
  {
    q: "Can I do this if I'm a solo operator?",
    a: "Yes. The assessment is designed for businesses of all sizes with 1 to 50 employees. Solo operators get just as much value.",
  },
  {
    q: "What happens after the assessment?",
    a: "You get a polished report with specific tools and a plan. If you want help implementing, we can talk about that separately. No pressure.",
  },
];

export default function AIAudit() {
  return (
    <div>
      {/* SECTION 1 -- Hero (Above Fold, NO CTA) */}
      <section className="relative flex min-h-[80vh] items-center overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            background: "radial-gradient(ellipse at 50% 0%, var(--page-accent) 0%, transparent 70%)",
          }}
        />
        <Container className="relative z-10 py-20 text-center">
          <h1
            className="font-display mx-auto max-w-4xl leading-[0.9] tracking-[0.02em]"
            style={{
              fontSize: "clamp(42px, 8vw, 84px)",
              color: "var(--text-0)",
            }}
          >
            Get a Custom AI Roadmap
            <br />
            <span className="glow-accent" style={{ color: "var(--page-accent)" }}>
              for Your Business in 48 Hours
            </span>
          </h1>
          <p
            className="mx-auto mt-6 max-w-xl text-[17px] font-light leading-relaxed"
            style={{ color: "var(--text-2)" }}
          >
            One call. One report. A dozen tools you can start using this week.
          </p>
          <div className="mt-12 animate-bounce">
            <ChevronDown className="mx-auto h-8 w-8 opacity-40" style={{ color: "var(--page-accent)" }} />
          </div>
        </Container>
      </section>

      {/* SECTION 2 -- The Opening (Warm, Direct) */}
      <section className="py-20 lg:py-28">
        <Container>
          <div className="mx-auto max-w-[720px] space-y-6 text-[16px] leading-[1.7]" style={{ color: "var(--text-1)" }}>
            <p>
              You know AI could help your business. You just don't have time to figure out which tools
              actually work. I'll do the research for you.
            </p>
            <p>
              I'm Jordan. I run Emailink, a studio that helps businesses use technology to grow.
              I talk to founders and operators every day who know they should be using AI tools --
              but get lost in the noise. A thousand new tools launch every week. Most are garbage.
              A few are game-changers.
            </p>
            <p>
              This assessment cuts through that noise. We talk for 45 minutes. I do the research.
              You get a custom roadmap with specific tools, prices, and a plan to start using them
              within four days.
            </p>
          </div>
        </Container>
      </section>

      {/* SECTION 3 -- How It Works (3 Steps) */}
      <section className="py-20 lg:py-24" style={{ background: "var(--surface-0)", borderTop: "1px solid var(--stroke-0)", borderBottom: "1px solid var(--stroke-0)" }}>
        <Container>
          <div className="mx-auto max-w-[720px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              How It Works
            </h2>
            <p className="mt-3 mb-12 text-[14px]" style={{ color: "var(--text-3)" }}>
              Three steps. Two days. One clear path forward.
            </p>

            <div className="space-y-10">
              {[
                {
                  icon: <Phone className="h-6 w-6" />,
                  step: "01",
                  title: "45-Minute Call",
                  desc: "I ask questions. You answer. No prep, no homework, no sales pitch. I just need to understand how your business works -- your team, your tools, your biggest time sinks.",
                },
                {
                  icon: <FileText className="h-6 w-6" />,
                  step: "02",
                  title: "I Do the Research",
                  desc: "Your answers get analyzed against thousands of tools. I match solutions to your exact pain points. No generic recommendations -- everything is specific to your business.",
                },
                {
                  icon: <CheckCircle className="h-6 w-6" />,
                  step: "03",
                  title: "You Get the Report + Follow-Up",
                  desc: "A polished report lands in your inbox within 48 hours. We meet for 30 minutes to walk through it. You leave with a plan you can execute immediately.",
                },
              ].map((item, i) => (
                <div key={i} className="flex gap-5">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[14px]" style={{ background: tint(12), color: "var(--page-accent)" }}>
                    {item.icon}
                  </div>
                  <div>
                    <span className="font-tech text-[11px] uppercase tracking-[0.12em]" style={{ color: "var(--text-3)" }}>
                      Step {item.step}
                    </span>
                    <h3 className="mt-1 text-[18px] font-semibold" style={{ color: "var(--text-0)" }}>
                      {item.title}
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed" style={{ color: "var(--text-2)" }}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 4 -- What's Inside the Report */}
      <section className="py-20 lg:py-28">
        <Container>
          <div className="mx-auto max-w-[720px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              What's Inside the Report
            </h2>
            <p className="mt-3 mb-12 text-[14px]" style={{ color: "var(--text-3)" }}>
              A complete AI action plan, tailored to your business.
            </p>

            <div className="grid gap-4 sm:grid-cols-2">
              {[
                {
                  label: "Executive Summary",
                  desc: "The one-page overview. What to use, why, and what it'll save you.",
                },
                {
                  label: "Impact-Effort Matrix",
                  desc: "Quick wins first. See exactly which tools give the biggest return for the least effort.",
                },
                {
                  label: "Recommended Solutions",
                  desc: "Specific tools with prices, setup time, and why they work for your situation.",
                },
                {
                  label: "4-Day Quick Wins Plan",
                  desc: "Day-by-day instructions for getting your first tool live in under a week.",
                },
                {
                  label: "Financial Impact",
                  desc: "Hours saved multiplied by your rate. You'll see the dollar value of every recommendation.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="rounded-[16px] p-6"
                  style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}
                >
                  <p className="font-display text-[15px] font-semibold" style={{ color: "var(--text-0)" }}>
                    {item.label}
                  </p>
                  <p className="mt-2 text-[13px] leading-relaxed" style={{ color: "var(--text-2)" }}>
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 5 -- Who This Is For */}
      <section className="py-20 lg:py-24" style={{ background: "var(--surface-0)", borderTop: "1px solid var(--stroke-0)", borderBottom: "1px solid var(--stroke-0)" }}>
        <Container>
          <div className="mx-auto max-w-[720px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              Who This Is For
            </h2>
            <div className="mt-10 space-y-4">
              {[
                "Business owners with 5 to 50 employees who know AI could help but don't have time to research it.",
                "You have a team and your hands are in too many pots. You need someone to just tell you what to use.",
                "You want a concrete plan, not a list of 50 tools you'll never try.",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="mt-1 h-2 w-2 flex-shrink-0 rounded-full" style={{ background: "var(--page-accent)" }} />
                  <p className="text-[15px] leading-relaxed" style={{ color: "var(--text-1)" }}>
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 6 -- Social Proof */}
      <section className="py-20 lg:py-28">
        <Container>
          <div className="mx-auto max-w-[720px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              What Clients Say
            </h2>
            <div className="mt-10 grid gap-6">
              <div className="rounded-[16px] p-6" style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" style={{ color: "var(--page-accent)" }} />
                  ))}
                </div>
                <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-1)" }}>
                  &ldquo;The average client identifies 8+ hours per week in time savings. That's over 400 hours
                  a year -- time you can put back into growing your business.&rdquo;
                </p>
                <p className="mt-3 text-[13px]" style={{ color: "var(--text-3)" }}>
                  -- Emailink Studio, client aggregate data
                </p>
              </div>

              <div className="rounded-[16px] p-6" style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" style={{ color: "var(--page-accent)" }} />
                  ))}
                </div>
                <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-1)" }}>
                  &ldquo;Jordan came back with three tools I'd never heard of that saved my team about 10 hours
                  a week. The report paid for itself on day one.&rdquo;
                </p>
                <p className="mt-3 text-[13px]" style={{ color: "var(--text-3)" }}>
                  -- Sarah M., e-commerce founder
                </p>
              </div>

              <div className="rounded-[16px] p-6" style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" style={{ color: "var(--page-accent)" }} />
                  ))}
                </div>
                <p className="mt-4 text-[15px] leading-relaxed" style={{ color: "var(--text-1)" }}>
                  &ldquo;I was skeptical about another 'AI assessment' but this was genuinely useful. No fluff,
                  just tools I could actually use with my existing team.&rdquo;
                </p>
                <p className="mt-3 text-[13px]" style={{ color: "var(--text-3)" }}>
                  -- Marcus R., agency owner
                </p>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 7 -- Pricing + CTA (Below Fold) */}
      <section className="py-20 lg:py-28" style={{ background: "var(--surface-0)", borderTop: "1px solid var(--stroke-0)" }}>
        <Container>
          <div className="mx-auto max-w-[720px] text-center">
            <h2 className="font-display text-[32px] leading-tight sm:text-[42px]" style={{ color: "var(--text-0)" }}>
              The AI Tools Assessment
            </h2>
            <p className="mt-4 font-display text-[52px] leading-none sm:text-[64px]" style={{ color: "var(--page-accent)" }}>
              $1,000
            </p>

            <div className="mx-auto mt-10 max-w-md space-y-4 text-left">
              {[
                "45-minute discovery call with me (no prep needed)",
                "Deep research against thousands of AI tools",
                "Polished custom report delivered in 48 hours",
                "30-minute follow-up call to walk through your plan",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: "var(--page-accent)" }} />
                  <p className="text-[15px]" style={{ color: "var(--text-1)" }}>
                    {item}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-[16px] p-5" style={{ background: tint(8), border: `1px solid ${tint(25)}` }}>
              <p className="text-[14px] italic leading-relaxed" style={{ color: "var(--text-2)" }}>
                &ldquo;If you don't find at least one tool in this report that's worth the investment,
                I'll refund every cent. No questions asked.&rdquo;
              </p>
            </div>

            <div className="mt-10 flex flex-col items-center gap-4">
              <button
                type="button"
                onClick={openCalendly}
                className="inline-flex h-14 items-center justify-center gap-3 rounded-[14px] px-10 text-[15px] font-semibold tracking-[0.04em] transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: "var(--page-accent)",
                  color: "var(--on-accent)",
                  boxShadow: `0 0 40px ${tint(30)}`,
                }}
              >
                Book Your Assessment -- $1,000
              </button>
              <p className="text-[12px]" style={{ color: "var(--text-3)" }}>
                Secure booking via Calendly
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 8 -- FAQ */}
      <section className="py-20 lg:py-28">
        <Container>
          <div className="mx-auto max-w-[720px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              Questions, answered.
            </h2>
            <p className="mt-3 mb-12 text-[14px]" style={{ color: "var(--text-3)" }}>
              The ones we get most often.
            </p>

            <div className="space-y-4">
              {FAQS.map((faq, i) => (
                <details
                  key={i}
                  className="group cursor-pointer rounded-[14px] transition-colors"
                  style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}
                >
                  <summary className="flex items-center justify-between p-5 text-[15px] font-medium" style={{ color: "var(--text-0)" }}>
                    {faq.q}
                    <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" style={{ color: "var(--text-3)" }} />
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-[14px] leading-relaxed" style={{ color: "var(--text-2)" }}>
                      {faq.a}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* SECTION 9 -- Final CTA */}
      <section className="py-20 lg:py-28 text-center" style={{ background: "var(--surface-0)", borderTop: "1px solid var(--stroke-0)" }}>
        <Container>
          <div className="mx-auto max-w-[600px]">
            <h2 className="font-display text-[28px] leading-tight sm:text-[36px]" style={{ color: "var(--text-0)" }}>
              Ready to find your AI tools?
            </h2>
            <p className="mt-4 text-[16px]" style={{ color: "var(--text-2)" }}>
              One call. One report. A clear path forward in 48 hours.
            </p>
            <p className="mt-1 mb-8 font-display text-[36px] sm:text-[42px]" style={{ color: "var(--page-accent)" }}>
              $1,000
            </p>
            <button
              type="button"
              onClick={openCalendly}
              className="inline-flex h-14 items-center justify-center gap-3 rounded-[14px] px-10 text-[15px] font-semibold tracking-[0.04em] transition-transform hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "var(--page-accent)",
                color: "var(--on-accent)",
                boxShadow: `0 0 40px ${tint(30)}`,
              }}
            >
              Book Your Assessment -- $1,000
            </button>
            <p className="mt-4 text-[13px]" style={{ color: "var(--text-3)" }}>
              Questions?{" "}
              <a href="mailto:jordan@emailink.io" className="underline transition-colors hover:text-[color:var(--text-0)]" style={{ color: "var(--page-accent)" }}>
                jordan@emailink.io
              </a>
            </p>
          </div>
        </Container>
      </section>

      {/* FOOTER -- minimal legal */}
      <footer className="py-8 text-center" style={{ borderTop: "1px solid var(--stroke-0)" }}>
        <Container>
          <p className="text-[12px]" style={{ color: "var(--text-3)" }}>
            Emailink Studio &mdash; 7901 4th St N STE 300, St. Petersburg, FL 33702, USA
          </p>
          <div className="mt-3 flex items-center justify-center gap-4 text-[12px]" style={{ color: "var(--text-3)" }}>
            <Link to="/privacy" className="underline transition-colors hover:text-[color:var(--text-1)]">
              Privacy
            </Link>
            <Link to="/terms" className="underline transition-colors hover:text-[color:var(--text-1)]">
              Terms
            </Link>
            <a href="mailto:jordan@emailink.io" className="underline transition-colors hover:text-[color:var(--text-1)]">
              jordan@emailink.io
            </a>
          </div>
        </Container>
      </footer>
    </div>
  );
}
